import React, {
  useMemo,
  useCallback,
}                        from 'react';
import {
  FormikProps,
  FieldInputProps,
}                        from 'formik';
import { TextField }     from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteProps,
}                        from '@material-ui/lab';
import CheckIcon         from '@material-ui/icons/Check';
import { makeStyles }    from '@material-ui/core/styles';
import { ISelectOption } from '@typings/components';

interface IAutoCompleteProps
  extends AutocompleteProps<any, any, any, any>{
  form        : FormikProps<any>;
  field       : FieldInputProps<any>;
  options     : ISelectOption[];
  error?      : boolean;
  label       : string;
  placeholder : string;
  margin      : string;
  marginTop   : number;
  width       : string;
  variant     : string;
  gray        : boolean;
}

const useStyles = makeStyles({
  inputRoot: {
    color: '#727272',
  },
  textFieldRootGray: {
    marginTop                                                                   : 0,
    marginBottom                                                                : 0,
    '& [class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]' : {
      padding         : '2px',
      backgroundColor : '#F5F5F5',
    },
  },
  paper: {
    color        : '#2C2C2C',
    boxShadow    : '1px 1px 5px #727272',
    padding      : '2px',
    borderRadius : 5,
  },
  option: {
    minHeight                 : 'auto',
    justifyContent            : 'space-between',
    alignItems                : 'center',
    padding                   : 8,
    '&[aria-selected="true"]' : { backgroundColor: 'transparent' },
    '&[data-focus="true"]'    : { backgroundColor: '#F0F0F0' },
  },
});

export const AutoComplete: React.FC<IAutoCompleteProps> = ({
  form,
  field,
  error,
  label,
  placeholder,
  width,
  margin,
  marginTop,
  variant,
  gray,
  options,
  ...props
}: IAutoCompleteProps): JSX.Element => {
  const classes = useStyles();

  const fieldValue = useMemo(
    (): any =>
      options.find(
        (option: ISelectOption): boolean =>
          option.value === field.value,
      ) ?? null,
    [field, options],
  );

  const onValueChange = useCallback(
    (option: ISelectOption): void => {
      form.setFieldValue(field.name, option?.value ?? null);
      form.setFieldTouched(field.name, true, false);
    },
    [field, form],
  );

  const onFieldBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>): void => {
      const { value } = event.target;

      if (!value) {
        form.setFieldValue(field.name, null);
      }
    },
    [field, form],
  );

  return (
    <Autocomplete
      {...field}
      {...props}
      value          = {fieldValue}
      options        = {options}
      style          = {{ width, marginTop }}
      classes        = {{
        inputRoot : classes.inputRoot,
        paper     : classes.paper,
        option    : classes.option,
      }}
      getOptionLabel = {(option: ISelectOption): string => option.label}
      renderOption   = {(option: ISelectOption): JSX.Element => (
        <>
          {option.label}
          <span>
            {field.value === option.value && <CheckIcon />}
          </span>
        </>
      )}
      renderInput    = {(params: any): JSX.Element => (
        <TextField
          {...params}
          label       = {label}
          placeholder = {placeholder}
          variant     = {variant}
          margin      = {margin}
          classes     = {{
            root: gray && classes.textFieldRootGray,
          }}
          onBlur      = {(): void => {
            form.setFieldTouched(field.name, true);
          }}
          error       = {error}
          inputProps  = {{
            ...params.inputProps,
            autoComplete: 'disabled', // disable autocomplete and autofill
          }}
          fullWidth
        />
      )}
      onBlur         = {onFieldBlur}
      onChange       = {(
        _event   : React.ChangeEvent<HTMLInputElement>,
        newValue : ISelectOption,
      ): void => onValueChange(newValue)}
    />
  );
};

AutoComplete.defaultProps = {
  gray                 : false,
  options              : [],
  margin               : 'dense',
  variant              : 'outlined',
  autoSelect           : true,
  autoComplete         : true,
  autoHighlight        : true,
  clearOnEscape        : true,
  disableClearable     : true,
  disableListWrap      : false,
  // disableOpenOnFocus   : true,
  disableCloseOnSelect : false,
  includeInputInList   : true,
  openText             : '',
  closeText            : '',
  clearText            : '',
  noOptionsText        : 'No matches...',
  disablePortal        : false,
};
